import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { WithBoundArgs } from '@glint/template';

import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import FormInputSearchComponent from 'webapp/components/form/input-search';
import type { SelectableListItem } from 'webapp/components/selectable-list';
import SelectableListLastRowComponent from 'webapp/components/selectable-list/last-row';
import SelectableListRowComponent from 'webapp/components/selectable-list/row';

import type FilterList from 'webapp/models-custom/filter-list';

interface Args {
	filter: FilterList<SelectableListItem>;
	selectedItems: SelectableListItem[];
	allSelected: boolean;
	showList: boolean;
	typedFilter?: string;
	onTypedFilterChange?: (value: string) => void;
	onSelectionChanged: (item: SelectableListItem, isSelected: boolean) => void;
	onBeforeSelectionChange?: (isChecked: boolean) => Promise<void>;
	onDeselectAll?: () => void;
	onSelectAll?: () => void;
}

export interface YieldedComponents {
	row?: WithBoundArgs<
		typeof SelectableListRowComponent,
		'item' | 'selectedItems' | 'typedFilter' | 'onSelectionChanged' | 'onBeforeSelectionChange'
	>;
	lastRow?: WithBoundArgs<typeof SelectableListLastRowComponent, never>;
}

export interface SelectableListListComponentInterface {
	Args: Args;
	Blocks: {
		default: [settings: YieldedComponents];
	};
}

export default class SelectableListListComponent extends Component<SelectableListListComponentInterface> {
	rowComponent = SelectableListRowComponent;
	lastRowComponent = SelectableListLastRowComponent;

	get groupSelectText(): string {
		return (this.args.allSelected ? 'Deselect' : 'Select') + ' all';
	}

	get showGroupSelect(): boolean {
		return !!this.args.onDeselectAll && !!this.args.onSelectAll;
	}

	@action
	groupSelect(): void {
		if (this.args.allSelected) {
			if (this.args.onDeselectAll) {
				this.args.onDeselectAll();
			}
		} else {
			if (this.args.onSelectAll) {
				this.args.onSelectAll();
			}
		}
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	changeSearch(value: string) {
		this.args.filter.typedFilter = value;
	}
	<template>
		<div class="selection-list {{unless @showList 'hidden'}}">
			{{#if @showList}}
				<FormInputSearchComponent
					@value={{@filter.typedFilter}}
					@onChange={{this.changeSearch}}
					@size="small"
				/>

				{{#if this.showGroupSelect}}
					<a
						role="button"
						class="group-select"
						{{on "click" this.groupSelect}}
					>{{this.groupSelectText}}
					</a>
				{{/if}}

				<div
					class="scrollable-content"
					{{visibleCheckContainer true}}
					{{didUpdate this.triggerContentChange @filter.filteredItems}}
				>
					<ul>
						{{#each @filter.filteredItems as |option|}}
							{{yield
								(hash
									row=(component
										this.rowComponent
										item=option
										selectedItems=@selectedItems
										typedFilter=@filter.typedFilter
										onSelectionChanged=@onSelectionChanged
										onBeforeSelectionChange=@onBeforeSelectionChange
									)
								)
							}}
						{{/each}}
						{{yield (hash lastRow=(component this.lastRowComponent))}}
					</ul>
				</div>

			{{/if}}
		</div>
	</template>
}
